import React, { useContext, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stepper,
  Step,
  useMediaQuery,
  StepLabel,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";
import GlobalStateContext from "../context/globalStateContext";
import UserContext from "../context/userContext";
import { firebase, storage, firestore } from "../firebaseConfig";
import { COLORS } from "../utils/COLORS";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { guidGenerator } from "../utils/HELPER_FUNCTIONS";
import { TextField } from "@mui/material";

const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ModalContent = styled(motion.div)(({ theme }) => ({
  backgroundColor: "#0F172A",
  borderRadius: "14px",
  border: "1px solid #1E293B",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  padding: "24px",
  outline: "none",
  width: "90%",
  maxWidth: "500px",
  maxHeight: "80vh",
  overflowY: "auto",
  color: "#FFFFFF",
}));

const UploadButton = styled(Button)(({ theme }) => ({
  marginTop: "16px",
  marginBottom: "16px",
  backgroundColor: "#1E293B",
  color: "#FFFFFF",
  border: "1px solid #334155",
  borderRadius: "8px",
  padding: "12px 16px",
  textTransform: "none",
  fontWeight: "500",
  "&:hover": {
    backgroundColor: "#334155",
    transform: "translateY(-1px)",
  },
}));

const ImagePreview = styled("img")({
  width: "100%",
  maxHeight: "200px",
  objectFit: "cover",
  borderRadius: "8px",
  marginTop: "16px",
  border: "1px solid #1E293B",
});

const StyledStepper = styled(Stepper)({
  "& .MuiStepLabel-root": {
    color: "#9CA3AF",
  },
  "& .MuiStepLabel-label": {
    color: "#9CA3AF",
    "&.Mui-active": {
      color: "#10B981",
    },
    "&.Mui-completed": {
      color: "#10B981",
    },
  },
  "& .MuiStepIcon-root": {
    color: "#374151",
    "&.Mui-active": {
      color: "#10B981",
    },
    "&.Mui-completed": {
      color: "#10B981",
    },
  },
});

const StyledTypography = styled(Typography)({
  color: "#FFFFFF",
  "&.MuiTypography-body1": {
    color: "#D1D5DB",
    textAlign: "center",
    marginBottom: "16px",
  },
});

const ClaimButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "8px",
  padding: "12px 16px",
  fontSize: "15px",
  fontWeight: "600",
  textTransform: "none",
  marginTop: "16px",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
  "&:disabled": {
    backgroundColor: "#374151",
    color: "#9CA3AF",
  },
});

const ClaimFundsModal = () => {
  const { user, setUser } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [proofImage, setProofImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    organizationName: "",
    email: "",
    phoneNumber: "",
    address: "",
    website: "",
    description: "",
  });
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));

  // Check if user needs to register rescue org first
  const needsRescueRegistration = !user?.rescueOrganizationId;
  // Check if user needs to connect Stripe
  const needsStripeConnection = !user?.stripe_account_id;

  // Auto-advance step when user gets Stripe connected after returning from Stripe
  useEffect(() => {
    // Only advance if user just got Stripe connected and we're on the Stripe step
    if (!needsStripeConnection && activeStep > 0) {
      let correctStep = 0;
      if (!needsRescueRegistration) correctStep++;
      if (!needsStripeConnection) correctStep++;
      setActiveStep(correctStep);
    }
  }, [user?.stripe_account_id]);

  const handleClose = () => {
    setGlobalState({ ...globalState, showClaimFundsModal: false });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProofImage(e.target.files[0]);
      // Calculate next step based on what's needed
      let nextStep = 0;
      if (needsRescueRegistration) nextStep++;
      if (needsStripeConnection) nextStep++;
      nextStep++; // Upload step
      setActiveStep(nextStep);
    }
  };

  const handleRescueRegistration = async () => {
    if (!formData.organizationName || !formData.email || !formData.phoneNumber || !formData.address) {
      alert("Please fill in all required fields");
      return;
    }

    setUploading(true);

    try {
      const rescueOrgId = guidGenerator();
      const rescueDetails = {
        organizationName: formData.organizationName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        website: formData.website || "",
        description: formData.description || "Rescue organization",
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      };

      // Save rescue organization to Firestore
      await firestore.collection("rescueOrganizations").doc(rescueOrgId).set(rescueDetails);

      // Update user's rescueOrganizationId in Firestore (where user data is now stored)
      await firestore.collection("users").doc(user.uid).update({
        rescueOrganizationId: rescueOrgId,
      });

      // Don't update local user state yet to keep step array consistent
      // setUser((prevUser) => ({
      //   ...prevUser,
      //   rescueOrganizationId: rescueOrgId,
      // }));

      // After rescue registration, always go to step 1
      // Step 1 will be either Stripe connection (if needed) or upload proof
      setActiveStep(1);
    } catch (error) {
      console.error("Error registering rescue organization:", error);
      alert("Error: " + (error.message || "An error occurred while registering. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const handleClaimFunds = async () => {
    if (!proofImage) {
      alert("Please upload proof of rescue");
      return;
    }

    // must have stripe id 
    if(!user?.stripe_account_id){
       alert("Please connect your Stripe account to claim funds.");
      return;
    }

    setUploading(true);

    try {
      const storageRef = storage.ref(
        `rescue_proofs/${globalState.selectedDog?.id}`
      );
      const uploadTask = await storageRef.put(proofImage);
      const imageUrl = await uploadTask.ref.getDownloadURL();
      const claimId = guidGenerator();

      // Save claim to Firestore
      await firestore.collection("claims").doc(claimId).set({
        claimId: claimId,
        claimStatus: "pending",
        proofOfRescueUrl: imageUrl,
        animalId: globalState.selectedDog?.animalId,
        slug: globalState.selectedDog?.slug,
        dogName: globalState.selectedDog?.name,
        claimedBy: user.uid,
        rescueOrganizationId: user.rescueOrganizationId,
        claimedAt: firebase.firestore.FieldValue.serverTimestamp(),
        claimedStripe: user?.stripe_account_id || "no stripe id",
      });

      // Calculate final step
      let finalStep = 0;
      if (needsRescueRegistration) finalStep++;
      if (needsStripeConnection) finalStep++;
      finalStep += 2; // Upload + Confirm steps
      setActiveStep(finalStep);
      setTimeout(() => {
        // Update user state now that we're done
        if (!user.rescueOrganizationId) {
          setUser((prevUser) => ({
            ...prevUser,
            rescueOrganizationId: user.rescueOrganizationId,
          }));
        }
        setGlobalState({ ...globalState, showClaimFundsModal: false });
      }, 3000);
    } catch (error) {
      console.error("Error claiming dog:", error);
      alert("An error occurred while claiming the dog. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Dynamic steps based on what user needs
  const steps = [];
  if (needsRescueRegistration) steps.push("Register Rescue");
  if (needsStripeConnection) steps.push("Connect Stripe");
  steps.push("Upload Proof", "Confirm", "Complete");

  return (
    <StyledModal
      open={globalState.showClaimFundsModal}
      onClose={handleClose}
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        onClick={handleClose}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <StyledTypography variant="h5" gutterBottom align="center" sx={{ fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>
            Claim {globalState.selectedDog?.name}'s Rescue
          </StyledTypography>

          <StyledStepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>

          {activeStep === 0 && needsRescueRegistration && (
            <>
              <StyledTypography variant="body1" sx={{ marginBottom: "12px", fontSize: "14px" }}>
                Quick rescue org setup (4 fields):
              </StyledTypography>
              <TextField
                fullWidth
                label="Organization Name"
                variant="outlined"
                size="small"
                name="organizationName"
                value={formData.organizationName}
                onChange={handleFormChange}
                required
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1E293B",
                    "& fieldset": { borderColor: "#334155" },
                    "&:hover fieldset": { borderColor: "#10B981" },
                    "&.Mui-focused fieldset": { borderColor: "#10B981" },
                  },
                  "& .MuiInputLabel-root": { color: "#9CA3AF" },
                  "& .MuiInputBase-input": { color: "#FFFFFF" },
                }}
              />
              <TextField
                fullWidth
                label="Contact Email"
                variant="outlined"
                size="small"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                required
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1E293B",
                    "& fieldset": { borderColor: "#334155" },
                    "&:hover fieldset": { borderColor: "#10B981" },
                    "&.Mui-focused fieldset": { borderColor: "#10B981" },
                  },
                  "& .MuiInputLabel-root": { color: "#9CA3AF" },
                  "& .MuiInputBase-input": { color: "#FFFFFF" },
                }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                variant="outlined"
                size="small"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleFormChange}
                required
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1E293B",
                    "& fieldset": { borderColor: "#334155" },
                    "&:hover fieldset": { borderColor: "#10B981" },
                    "&.Mui-focused fieldset": { borderColor: "#10B981" },
                  },
                  "& .MuiInputLabel-root": { color: "#9CA3AF" },
                  "& .MuiInputBase-input": { color: "#FFFFFF" },
                }}
              />
              <TextField
                fullWidth
                label="Address"
                variant="outlined"
                size="small"
                name="address"
                value={formData.address}
                onChange={handleFormChange}
                required
                sx={{
                  marginBottom: "16px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#1E293B",
                    "& fieldset": { borderColor: "#334155" },
                    "&:hover fieldset": { borderColor: "#10B981" },
                    "&.Mui-focused fieldset": { borderColor: "#10B981" },
                  },
                  "& .MuiInputLabel-root": { color: "#9CA3AF" },
                  "& .MuiInputBase-input": { color: "#FFFFFF" },
                }}
              />
              <ClaimButton
                variant="contained"
                fullWidth
                onClick={handleRescueRegistration}
                disabled={uploading}
              >
                {uploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Continue"
                )}
              </ClaimButton>
            </>
          )}

          {/* Stripe Connection Step */}
          {needsStripeConnection && activeStep === (needsRescueRegistration ? 1 : 0) && (
            <>
              <StyledTypography variant="body1" sx={{ marginBottom: "16px" }}>
                Connect your Stripe account to receive rescue funds.
              </StyledTypography>
              <ClaimButton
                variant="contained"
                fullWidth
                onClick={() => {
                  const stripeConnectUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=ca_QxOuyOM0CSgUDCuM5Gm6NMYmDNChcxdX&scope=read_write&state=${user.uid}`;
                  window.open(stripeConnectUrl, '_blank');
                }}
              >
                Connect Stripe Account
              </ClaimButton>
              <StyledTypography variant="body2" sx={{ marginTop: "12px", fontSize: "12px", color: "#9CA3AF", textAlign: "center" }}>
                You'll be redirected to Stripe to connect your account. Return here after connecting.
              </StyledTypography>
            </>
          )}

          {/* Upload Proof Step */}
          {(() => {
            let uploadStepIndex = 0;
            if (needsRescueRegistration) uploadStepIndex++;
            if (needsStripeConnection) uploadStepIndex++;
            return activeStep === uploadStepIndex;
          })() && (
            <>
              <StyledTypography variant="body1">
                Please upload proof of rescue to claim funds.
              </StyledTypography>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="raised-button-file"
                type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="raised-button-file">
                <UploadButton
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUploadIcon sx={{ color: "#FFFFFF" }} />}
                >
                  Upload Proof of Rescue
                </UploadButton>
              </label>
            </>
          )}

          {/* Confirm Step */}
          {(() => {
            let confirmStepIndex = 0;
            if (needsRescueRegistration) confirmStepIndex++;
            if (needsStripeConnection) confirmStepIndex++;
            confirmStepIndex++; // Upload step
            return activeStep === confirmStepIndex;
          })() && (
            <>
              <StyledTypography variant="body1">
                Confirm your proof of rescue and claim funds.
              </StyledTypography>
              {proofImage && (
                <ImagePreview
                  src={URL.createObjectURL(proofImage)}
                  alt="Proof of Rescue"
                />
              )}
              <ClaimButton
                variant="contained"
                fullWidth
                onClick={handleClaimFunds}
                disabled={uploading}
              >
                {uploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Claim ${globalState.selectedDog?.name}`
                )}
              </ClaimButton>
            </>
          )}

          {/* Complete Step */}
          {(() => {
            let completeStepIndex = 0;
            if (needsRescueRegistration) completeStepIndex++;
            if (needsStripeConnection) completeStepIndex++;
            completeStepIndex += 2; // Upload + Confirm steps
            return activeStep === completeStepIndex;
          })() && (
            <>
              <StyledTypography variant="h6" gutterBottom align="center" sx={{ fontSize: "20px", fontWeight: "600", color: "#10B981", marginBottom: "16px" }}>
                Claim Submitted Successfully!
              </StyledTypography>
              <StyledTypography variant="body1">
                Thank you for rescuing {globalState.selectedDog?.name}. We'll
                review & process your claim ASAP.
              </StyledTypography>
            </>
          )}
        </ModalContent>
      </motion.div>
    </StyledModal>
  );
};

export default ClaimFundsModal;
